/**
 * Sales API Layer.
 *
 * React Query hooks for sales orders, customers, and dashboard KPIs.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";
import type {
  ApplySOSchemesResponse,
  Customer,
  CustomerCreate,
  DashboardKPIs,
  SOLineChargeCreate,
  SOLineCreate,
  SOLineUpdate,
  SOOrderChargeCreate,
  SalesOrder,
  SalesOrderCreate,
  SalesOrderUpdate,
} from "@/types/sales-order";

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function useCustomers(page = 1, pageSize = 100) {
  return useQuery<PaginatedResponse<Customer>>({
    queryKey: ["customers", { page, pageSize }],
    queryFn: async () => {
      const resp = await apiClient.get(
        `/api/v1/customers?page=${page}&page_size=${pageSize}`,
      );
      return resp.data as PaginatedResponse<Customer>;
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation<Customer, Error, CustomerCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/customers", data)).data as Customer,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

// ---------------------------------------------------------------------------
// Sales Orders
// ---------------------------------------------------------------------------

interface UseSOsParams {
  page?: number;
  pageSize?: number;
  status?: string;
}

export function useSalesOrders(params: UseSOsParams = {}) {
  const { page = 1, pageSize = 25, status } = params;
  return useQuery<PaginatedResponse<SalesOrder>>({
    queryKey: ["sales-orders", { page, pageSize, status }],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("page", String(page));
      sp.set("page_size", String(pageSize));
      if (status) sp.set("status", status);
      const resp = await apiClient.get(`/api/v1/sales-orders?${sp}`);
      return resp.data as PaginatedResponse<SalesOrder>;
    },
  });
}

export function useSalesOrder(soId: string | undefined) {
  return useQuery<SalesOrder>({
    queryKey: ["sales-orders", soId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/sales-orders/${soId}`)).data as SalesOrder,
    enabled: !!soId,
  });
}

export function useCreateSalesOrder() {
  const qc = useQueryClient();
  return useMutation<SalesOrder, Error, SalesOrderCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/sales-orders", data)).data as SalesOrder,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-orders"] }),
  });
}

export function useAddSOLine(soId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SOLineCreate>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/sales-orders/${soId}/lines`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-orders", soId] }),
  });
}

export function useRemoveSOLine(soId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (lineId) => {
      await apiClient.delete(`/api/v1/sales-orders/${soId}/lines/${lineId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-orders", soId] }),
  });
}

export function useConfirmSalesOrder(soId: string) {
  const qc = useQueryClient();
  return useMutation<SalesOrder, Error, void>({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/sales-orders/${soId}/confirm`))
        .data as SalesOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useRecordSOPayment(soId: string) {
  const qc = useQueryClient();
  return useMutation<
    SalesOrder,
    Error,
    { payment_method: string; payment_reference?: string }
  >({
    mutationFn: async (data) =>
      (
        await apiClient.post(
          `/api/v1/sales-orders/${soId}/record-payment`,
          data,
        )
      ).data as SalesOrder,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sales-orders"] }),
  });
}

/**
 * Update a sales order — discount, trade-in, dates, salesperson, commission.
 *
 * Invalidates the list as well as the order: a discount changes the total, and
 * the list shows totals.
 */
export function useUpdateSalesOrder(soId: string) {
  const qc = useQueryClient();
  return useMutation<SalesOrder, Error, SalesOrderUpdate>({
    mutationFn: async (data) =>
      (await apiClient.put(`/api/v1/sales-orders/${soId}`, data))
        .data as SalesOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

/** Edit a line's description, quantity, price, discount or cost. */
export function useUpdateSOLine(soId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { lineId: string; data: SOLineUpdate }>({
    mutationFn: async ({ lineId, data }) =>
      (
        await apiClient.put(
          `/api/v1/sales-orders/${soId}/lines/${lineId}`,
          data,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Charges — per line, and for the order as a whole
// ---------------------------------------------------------------------------

/**
 * Add an itemised charge to a line.
 *
 * `is_taxable` is required, matching the server. It is not defaulted anywhere in
 * this chain on purpose.
 */
export function useAddSOLineCharge(soId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { lineId: string } & SOLineChargeCreate>({
    mutationFn: async ({ lineId, ...data }) =>
      (
        await apiClient.post(
          `/api/v1/sales-orders/${soId}/lines/${lineId}/charges`,
          data,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

export function useDeleteSOLineCharge(soId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { lineId: string; chargeId: string }>({
    mutationFn: async ({ lineId, chargeId }) => {
      await apiClient.delete(
        `/api/v1/sales-orders/${soId}/lines/${lineId}/charges/${chargeId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

/** Add an ad hoc charge to the order rather than to one line. */
export function useAddSOOrderCharge(soId: string) {
  const qc = useQueryClient();
  return useMutation<SalesOrder, Error, SOOrderChargeCreate>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/sales-orders/${soId}/charges`, data))
        .data as SalesOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

export function useDeleteSOOrderCharge(soId: string) {
  const qc = useQueryClient();
  return useMutation<SalesOrder, Error, string>({
    mutationFn: async (chargeId) =>
      (
        await apiClient.delete(
          `/api/v1/sales-orders/${soId}/charges/${chargeId}`,
        )
      ).data as SalesOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

/**
 * Apply every applicable scheme, routing each to its declared level.
 *
 * Returns a summary alongside the order so the caller can report what landed
 * rather than inferring it from the totals.
 */
export function useApplySOSchemes(soId: string) {
  const qc = useQueryClient();
  return useMutation<ApplySOSchemesResponse, Error, void>({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/sales-orders/${soId}/apply-schemes`))
        .data as ApplySOSchemesResponse,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-orders", soId] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useDashboardKPIs() {
  return useQuery<DashboardKPIs>({
    queryKey: ["dashboard", "kpis"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/dashboard/kpis")).data as DashboardKPIs,
  });
}
